/* eslint-disable max-lines, no-unused-vars */
/**
 * Common-Org blueprint deep-copy walker.
 * Per A-0012.02 SPEC §7.1 + UF-15.
 *
 * On Deploy:
 *   1. Fetch the blueprint assistant from Common Org.
 *   2. Walk every opcode the blueprint references; for each:
 *      a. Inspect its steps to find LLM (Instruction) / API / MCP refs.
 *      b. For each LLM ref (unique_name + model_type): if user's (org, sub)
 *         doesn't already have one with that unique_name, copy it from
 *         Common Org. Reference stays by name — no rewrite needed.
 *      c. For each API ref (api_id): copy + rewrite api_id in the step.
 *      d. For each MCP ref (connection_id): copy + rewrite connection_id.
 *      e. Copy the opcode itself with a new opcode_id (keeps a fresh
 *         per-org slug so two assistants in the same sub-org don't share
 *         the opcode mutably).
 *   3. Build the new assistant doc — strip server-managed fields, rewrite
 *      opcode references via idMap, surface non-copyable artifacts as
 *      warnings (Knowledge collections, Channels).
 *   4. POST /create-assistant — server auto-generates assistant_id from title.
 *
 * Per UF-2: no schema changes, no new endpoints. Pure composition.
 */
import {
    opcodeAssistantService
} from 'shared/api';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
    instructionService
} from 'shared/api/services/BEX/instruction.service';
import {
    COMMON_ORG_ID, COMMON_SUB_ORG_ID
} from '../config/featureFlags';

// Server REJECTS these on create — verified empirically against current Joi
const ASSISTANT_FORBIDDEN = [
    'assistant_id',
    '_id',
    'created', 'createdBy',
    'updated', 'updatedBy',
    'assistant_name', // forbidden per A-0007.03 Addendum
    'opcodes_v2', // forbidden per Addendum
    'metadata' // not allowed in current Joi
];

// Opcode fields the server rejects or auto-fills
const OPCODE_FORBIDDEN = [
    '_id',
    'audit',
    'created', 'createdBy',
    'updated', 'updatedBy',
    'resources_used',
    'engine_capabilities_required',
    'metadata', // not allowed in current Joi
    'org_id', 'sub_org_id' // server uses URL params, rejects in body
];

// Instruction (LLM) fields the server rejects or auto-fills
const INSTRUCTION_FORBIDDEN = [
    '_id',
    'created', 'createdBy',
    'updated', 'updatedBy',
    'creation_timestamp', 'modified_timestamp',
    'modified_by',
    'user_id', // re-stamped to deploying user on copy
];

function strip(obj, keys) {
    const out = {
        ...obj
    };
    for (const k of keys) delete out[k];
    return out;
}

// Normalize legacy type aliases the user-org Joi rejects.
// Common-Org blueprints sometimes carry `dict`/`list`/`int`/`float`/`bool`
// — coerce to the canonical Joi enum [string, number, integer, boolean, object, array, null].
const TYPE_ALIASES = {
    dict: 'object',
    list: 'array',
    int: 'integer',
    float: 'number',
    bool: 'boolean',
    str: 'string',
    text: 'string'
};
function normalizeType(t) {
    if (!t) return t;
    const key = String(t).toLowerCase();
    return TYPE_ALIASES[key] || t;
}
function normalizeTypedRow(row) {
    if (!row || typeof row !== 'object') return row;
    return {
        ...row,
        type: normalizeType(row.type)
    };
}
// Step-common fields the user-org Joi schema rejects (A-0008.03 additions
// that haven't fully landed on this server). Strip them — the user can add
// them back via the OpCode builder once the server catches up.
// Verified empirically against bp_approve_policy: description + stream were
// the rejection cause.
const STEP_FORBIDDEN = ['description', 'stream', 'timeout_ms', 'on_error', 'role', 'when', 'for', 'repeat_until'];

function normalizeStep(s) {
    if (!s || typeof s !== 'object') return s;
    let next = {
        ...s
    };
    // Strip step-common fields the user-org Joi may reject
    for (const k of STEP_FORBIDDEN) delete next[k];
    // NOTE: step.output_type uses Python-style names (dict/list/int/...) and
    // the user-org Joi enum is the Python set. Do NOT convert it to JSON Schema
    // names here — opcode-level inputs[].type / outputs[].type are converted,
    // but step.output_type stays as-is.
    // Recurse into parallel branches
    if (next.kind === 'parallel' && Array.isArray(next.branches)) {
        next.branches = next.branches.map(normalizeStep);
    }
    return next;
}

function normalizeOpcodeShape(opcode) {
    const out = {
        ...opcode
    };
    if (Array.isArray(out.inputs)) out.inputs = out.inputs.map(normalizeTypedRow);
    if (Array.isArray(out.outputs)) out.outputs = out.outputs.map(normalizeTypedRow);
    if (Array.isArray(out.steps)) out.steps = out.steps.map(normalizeStep);
    if (Array.isArray(out.definitions)) out.definitions = out.definitions.map(normalizeStep);
    if (Array.isArray(out.flows)) {
        out.flows = out.flows.map((f) => {
            const fn = {
                ...f
            };
            if (Array.isArray(fn.output)) fn.output = fn.output.map(normalizeTypedRow);
            if (Array.isArray(fn.outputs)) fn.outputs = fn.outputs.map(normalizeTypedRow);
            if (Array.isArray(fn.steps)) fn.steps = fn.steps.map(normalizeStep);
            return fn;
        });
    }
    return out;
}

function makeOpcodeId(originalId) {
    const base = (originalId || 'opcode').toString();
    const suffix = Math.random().toString(36).slice(2, 8);
    const safe = base.toLowerCase().replace(/[^a-z0-9_-]/g, '-').slice(0, 40);
    return `${safe}-${suffix}`;
}

function detailFromError(err) {
    return err?.response?.data?.message
        || err?.response?.data?.error
        || (err?.response?.data ? JSON.stringify(err.response.data) : null)
        || err?.message
        || String(err);
}

/* ---------------- LLM (Instruction) deep-copy ---------------- */

async function ensureLLMInUserOrg(uniqueName, modelType, userOrg, userSub, userId, warnings) {
    if (!uniqueName || !modelType) return;

    // Already there?
    try {
        const existingRes = await instructionService.getModel(userOrg, userSub, modelType, uniqueName);
        const existing = unwrap(existingRes);
        if (existing && (existing.unique_name || existing._id)) return; // present
    } catch {
        // 404 / not found → fall through to copy
    }

    // Fetch from Common Org
    let original;
    try {
        const res = await instructionService.getModel(COMMON_ORG_ID, COMMON_SUB_ORG_ID, modelType, uniqueName);
        original = unwrap(res);
    } catch (err) {
        warnings.push(
            `LLM "${uniqueName}" (${modelType}) not found in Common Org and not in your org. `
            + 'You may need to create it manually before this opcode can run.'
        );
        return;
    }
    if (!original || !original.unique_name) {
        warnings.push(`LLM "${uniqueName}" (${modelType}) returned empty from Common Org.`);
        return;
    }

    // Build clean payload — keep unique_name (that's the reference key)
    const payload = strip(original, INSTRUCTION_FORBIDDEN);
    payload.org_id = userOrg;
    payload.sub_org_id = userSub;
    if (userId) payload.user_id = String(userId);

    try {
        await instructionService.createModel(payload);
    } catch (err) {
        warnings.push(
            `Could not copy LLM "${uniqueName}" (${modelType}) from Common Org: ${detailFromError(err)}. `
            + 'Create it manually in Library → Instructions before this opcode can run.'
        );
    }
}

/* ---------------- Walk opcode steps to find refs ---------------- */

function* walkSteps(opcode) {
    const visit = (steps) => {
        if (!Array.isArray(steps)) return [];
        return steps;
    };
    for (const s of visit(opcode.steps)) yield s;
    for (const s of visit(opcode.definitions)) yield s;
    for (const f of (opcode.flows || [])) {
        for (const s of visit(f.steps)) yield s;
    }
}

async function copyOpcodeReferencedAssets(opcode, userOrg, userSub, userId, warnings) {
    // Walk steps and ensure referenced LLMs/APIs/MCPs exist in user's org
    const seenLLMs = new Set();
    for (const step of walkSteps(opcode)) {
        if (!step) continue;
        if (step.kind === 'llm') {
            const key = `${step.model_type || ''}:${step.unique_name || ''}`;
            if (step.unique_name && step.model_type && !seenLLMs.has(key)) {
                seenLLMs.add(key);
                await ensureLLMInUserOrg(step.unique_name, step.model_type, userOrg, userSub, userId, warnings);
            }
        }
        // APIs + MCPs deep-copy: needs api_id / connection_id rewrite which
        // the current blueprints don't yet exercise (bp_blank uses LLM only).
        // Surface as warning if encountered so user knows what to do.
        if (step.kind === 'api' && step.api_id) {
            warnings.push(
                `Opcode "${opcode.opcode_id}" step "${step.id}" references API "${step.api_id}". `
                + 'API deep-copy not yet implemented in V1 walker. '
                + 'Create the API in Library → APIs and update the step.'
            );
        }
        if (step.kind === 'tool' && step.tool_ref?.connection_id) {
            warnings.push(
                `Opcode "${opcode.opcode_id}" step "${step.id}" references MCP connection "${step.tool_ref.connection_id}". `
                + 'MCP deep-copy not yet implemented in V1 walker. '
                + 'Create the MCP in Library → MCP Servers and update the step.'
            );
        }
        if (step.kind === 'rag' && step.inputs?.collection_id) {
            warnings.push(
                `Opcode "${opcode.opcode_id}" step "${step.id}" references Knowledge collection "${step.inputs.collection_id}". `
                + 'Collections live in Knowledge Builder and are NOT auto-copied. Seed it in DE Studio → Knowledge.'
            );
        }
    }
}

/* ---------------- Opcode deep-copy ---------------- */

/** Pull the actual body off whatever the bex client returned. */
function unwrap(res) {
    return res?.data ?? res;
}

async function copyOpcodeFromCommon(opcodeId, userOrg, userSub, userId, warnings) {
    const getRes = await jsonOpcodeService.getJSONOpcodeById({
        organizationId: COMMON_ORG_ID,
        subOrganizationId: COMMON_SUB_ORG_ID,
        opcodeId
    });
    const original = unwrap(getRes);
    if (!original || !original.opcode_id) {
        throw new Error(`Opcode "${opcodeId}" not found in Common Org`);
    }

    // Ensure referenced LLMs exist in user's org (deep-copy by name)
    await copyOpcodeReferencedAssets(original, userOrg, userSub, userId, warnings);

    // Strip server-managed fields. NOTE: server REGENERATES `opcode_id` on
    // create — the value we send is ignored. We MUST read the returned doc
    // for the canonical new id.
    let newOpcode = strip(original, OPCODE_FORBIDDEN);
    delete newOpcode.opcode_id; // explicit — server-generated
    // Normalize legacy types (Common-Org has `dict`, user-org Joi rejects)
    newOpcode = normalizeOpcodeShape(newOpcode);

    try {
        const createRes = await jsonOpcodeService.createJSONOpcode({
            organizationId: userOrg,
            subOrganizationId: userSub,
            opcode: newOpcode
        });
        const created = unwrap(createRes);
        const serverOpcodeId = created?.opcode_id;
        if (!serverOpcodeId) {
            throw new Error('Server did not return new opcode_id on create');
        }
        return serverOpcodeId;
    } catch (err) {
        const detail = detailFromError(err);
        const enriched = new Error(detail);
        enriched.cause = err;
        throw enriched;
    }
}

/* ---------------- Top-level deploy ---------------- */

export async function deployBlueprint(blueprintAssistantId, userOrg, userSub, newName, userId) {
    const warnings = [];
    const errors = [];

    // 1. Fetch the blueprint
    let blueprint;
    try {
        const res = await opcodeAssistantService.getOpcodeAssistant(
            COMMON_ORG_ID, COMMON_SUB_ORG_ID, blueprintAssistantId
        );
        blueprint = res?.data;
    } catch (err) {
        errors.push(`Could not fetch blueprint: ${detailFromError(err)}`);
        return {
            newAssistantId: null,
            warnings,
            errors
        };
    }
    if (!blueprint) {
        errors.push('Blueprint not found in Common Org.');
        return {
            newAssistantId: null,
            warnings,
            errors
        };
    }

    // 2. Deep-copy each referenced opcode
    const idMap = {};
    if (Array.isArray(blueprint.opcodes)) {
        for (const opRef of blueprint.opcodes) {
            const oldId = opRef?.opcode_id;
            if (!oldId) continue;
            try {
                const newId = await copyOpcodeFromCommon(oldId, userOrg, userSub, userId, warnings);
                idMap[oldId] = newId;
            } catch (err) {
                warnings.push(
                    `Could not copy opcode "${oldId}": ${detailFromError(err)}. `
                    + 'Assistant will be created without it.'
                );
            }
        }
    }

    // 3. Build the assistant doc — strip + rewrite refs
    const newAssistant = strip(blueprint, ASSISTANT_FORBIDDEN);
    newAssistant.title = newName || `${blueprint.title || 'Assistant'} (copy)`;
    newAssistant.org_id = userOrg;
    newAssistant.sub_org_id = userSub;
    newAssistant.assistant_token = 'sk-ast-' + Math.random().toString(36).slice(2, 18) + Math.random().toString(36).slice(2, 10);

    if (Array.isArray(newAssistant.opcodes)) {
        newAssistant.opcodes = newAssistant.opcodes
            .map((op) => {
                const newId = idMap[op.opcode_id];
                if (!newId) return null;
                return {
                    ...op,
                    opcode_id: newId
                };
            })
            .filter(Boolean);
    }
    if (newAssistant.default_opcode) {
        if (idMap[newAssistant.default_opcode]) {
            newAssistant.default_opcode = idMap[newAssistant.default_opcode];
        } else {
            warnings.push(
                `Default opcode "${newAssistant.default_opcode}" was not copied; cleared from new assistant.`
            );
            delete newAssistant.default_opcode;
        }
    }

    // Surface non-copyable artifacts as warnings
    if (newAssistant.knowledge?.length) {
        warnings.push(
            `Blueprint references ${newAssistant.knowledge.length} knowledge collection(s). `
            + 'Collections are NOT auto-copied (Knowledge Builder owns them). '
            + 'Seed them in DE Studio → Knowledge and re-bind in this assistant.'
        );
    }
    if (newAssistant.channels?.authorized_channels?.length) {
        warnings.push(
            `Blueprint expects ${newAssistant.channels.authorized_channels.length} channel binding(s). `
            + 'Channels are tenant-specific; create them in Library → Channels and re-bind.'
        );
    }
    if (newAssistant.data_connections?.length) {
        warnings.push(
            `Blueprint references ${newAssistant.data_connections.length} API connection(s). `
            + 'Create the matching APIs in Library → APIs and re-bind.'
        );
    }

    // 4. Create the assistant
    try {
        const createRes = await opcodeAssistantService.createOpcodeAssistant(newAssistant);
        const newId = createRes?.data?.assistant_id;
        if (!newId) {
            errors.push('Server did not return new assistant_id.');
            return {
                newAssistantId: null,
                warnings,
                errors
            };
        }
        return {
            newAssistantId: newId,
            warnings,
            errors
        };
    } catch (err) {
        errors.push(`Could not create assistant: ${detailFromError(err)}`);
        return {
            newAssistantId: null,
            warnings,
            errors
        };
    }
}

/**
 * Fetch all blueprints from Common Org for the picker UI.
 */
export async function listCommonOrgBlueprints() {
    try {
        const res = await opcodeAssistantService.listOpcodeAssistants(
            COMMON_ORG_ID, COMMON_SUB_ORG_ID
        );
        return Array.isArray(res?.data) ? res.data : [];
    } catch (err) {
        console.error('Failed to load Common Org blueprints:', err);
        return [];
    }
}
